import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import yup from '../../../../../validation/yup';
import { CustomModal, ModalBody, ModalHeader, ModalActions } from '../../../../../base/components/CustomModal';
import Button from '../../../../../base/components/Button';
import { BUTTON_COLORS } from '../../../../../base/components/Button/appearance';
import FormikInput from '../../../../../base/components/FormikInput';
import FormikReactSelect from '../../../../../base/components/FormikReactSelect';
import FormikTextarea from '../../../../../base/components/FormikTextarea';
import { useService } from '../../../../../base/hooks/useService';
import NotificationsService from '../../../../../services/NotificationsService';
import ToastService from '../../../../../services/ToastService';
import { useLoading } from '../../../../../base/hooks/useLoading';
import { TARGET_OPTIONS } from '../constants';

const validationSchema = yup.object().shape({
  title: yup.string().required('Title is required').trim(),
  body: yup.string().required('Body is required').trim(),
  target: yup.number().required('Target is required'),
  timeToSend: yup.string().when('hasTimeToSend', {
    is: true,
    then: yup.string()
      .required('Time to send is required')
      .matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Please enter a valid time format (HH:MM, e.g., 14:30)'),
    otherwise: yup.string()
  })
});

export default function EditNotificationModal({ 
  isOpen, 
  onClose, 
  notification,
  onSuccess
}) {
  const notificationsService = useService(NotificationsService);
  const toastService = useService(ToastService);
  const [isLoading, { registerPromise }] = useLoading();

  const initialValues = {
    title: notification?.title || '',
    body: notification?.body || '',
    target: notification?.target || '',
    timeToSend: notification?.timeToSend || '',
    hasTimeToSend: Boolean(notification?.timeToSend)
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      const payload = {
        title: values.title,
        body: values.body,
        target: values.target,
        ...(values.hasTimeToSend && { timeToSend: values.timeToSend })
      };

      const updatedNotification = await registerPromise(notificationsService.updateNotification(notification.id, payload));
      
      toastService.success('Notification updated successfully');
      onSuccess(updatedNotification);
      onClose();
    } catch (error) {
      console.error('Error updating notification:', error);
      toastService.error('Failed to update notification');
    } finally {
      setSubmitting(false);
    }
  };

  if (!notification) return null;

  return (
    <CustomModal isOpen={isOpen} className="edit-notification-modal" size="md">
      <ModalHeader onClose={onClose}>
        Edit Notification
      </ModalHeader>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, handleSubmit, isSubmitting, setFieldValue }) => {
          const hasErrors = Object.keys(errors).length > 0;
          const isSubmitDisabled = isSubmitting || hasErrors;

          return (
            <form onSubmit={handleSubmit}>
            <ModalBody>
              <div className="d-flex flex-column gap-3">
                <div>
                  <FormikInput
                    name="title"
                    label="Title *"
                    placeholder="Enter notification title"
                  />
                  <small className="text-muted">
                    {values.title.length} characters
                  </small>
                </div>

                <div>
                  <FormikTextarea
                    name="body"
                    label="Body *"
                    placeholder="Enter notification body"
                    rows={4}
                  />
                  <small className="text-muted">
                    {values.body.length} characters
                  </small>
                </div>

                <FormikReactSelect
                  name="target"
                  label="Target *"
                  placeholder="Select target"
                  options={TARGET_OPTIONS}
                  setFieldValue={setFieldValue}
                  value={TARGET_OPTIONS.find(option => option.value === values.target)}
                />

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="hasTimeToSend"
                    checked={values.hasTimeToSend}
                    onChange={(e) => {
                      setFieldValue('hasTimeToSend', e.target.checked);
                      if (!e.target.checked) {
                        setFieldValue('timeToSend', '');
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="hasTimeToSend">
                    Set time to send
                  </label>
                </div>

                {values.hasTimeToSend && (
                  <FormikInput
                    name="timeToSend"
                    label="Time to send *"
                    placeholder="Enter time (HH:MM)"
                    type="time"
                  />
                )}
              </div>
            </ModalBody>
            
            <ModalActions>
              <Button
                color={BUTTON_COLORS.primaryOutline}
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color={BUTTON_COLORS.primary}
                type="submit"
                disabled={isSubmitDisabled}
                loading={isLoading}
              >
                Save Changes
              </Button>
            </ModalActions>
          </form>
        );
        }}
      </Formik>
    </CustomModal>
  );
}
